import { useCallback, useState } from "react";
import moment from "moment";
import { getSessionBreaks } from "../../services/attendanceService";
import { hmsToSec, secToHhmm } from "../../utils/liveHours";

const fmtClock = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm a") : null);

/**
 * Shared state for the expandable break breakdown used by the Dashboard, History and
 * Member-profile tables. One row is open at a time; each session's breaks are fetched once
 * (on first expand) and then cached, so re-opening is instant and the 30s list polls stay
 * cheap.
 */
export const useBreakDetails = () => {
  const [openId, setOpenId] = useState(null);
  const [breaksById, setBreaksById] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  const toggle = useCallback(
    async (attendanceId) => {
      if (!attendanceId) return;
      if (String(openId) === String(attendanceId)) return setOpenId(null); // collapse
      setOpenId(attendanceId);
      if (breaksById[attendanceId]) return; // already cached
      setLoadingId(attendanceId);
      const breaks = await getSessionBreaks(attendanceId);
      setBreaksById((prev) => ({ ...prev, [attendanceId]: breaks || [] }));
      setLoadingId(null);
    },
    [openId, breaksById]
  );

  const isOpen = useCallback((attendanceId) => String(openId) === String(attendanceId), [openId]);

  return { openId, isOpen, toggle, breaksById, loadingId };
};

/**
 * The "Break" table cell: total break time, the ×N count chip, and the toggle icon that
 * opens the break/back breakdown. Shows a plain dash when the session had no breaks.
 */
export const BreakCell = ({ log, breakStr, expanded, onToggle }) => {
  if (!log.breakCount) return <span style={{ color: "var(--muted-2)" }}>—</span>;
  return (
    <>
      <span className="log-time">{breakStr}</span>
      <span className="brk-chip">×{log.breakCount}</span>
      <button
        type="button"
        className={`brk-toggle ${expanded ? "on" : ""}`}
        title={expanded ? "Hide break times" : "Show break & back times"}
        aria-expanded={expanded}
        onClick={(e) => {
          e.stopPropagation(); // rows themselves navigate to the profile
          onToggle(log.id);
        }}
      >
        <svg>
          <use href="#i-chev-r" />
        </svg>
      </button>
    </>
  );
};

/**
 * Full-width panel rendered right under an expanded row: every break/back pair of that
 * session. A break that is still running has no end time yet, so it ticks from its start.
 */
export const BreakDetailRow = ({ breaks, loading }) => {
  // While the session's breaks are being fetched, render nothing rather than a
  // "Loading…" flash — the panel simply appears once the data (usually cached) is ready.
  if (loading) return null;
  if (!breaks || breaks.length === 0) return <div className="brk-detail muted">No break records for this session.</div>;

  return (
    <div className="brk-detail">
      <div className="brk-detail-head">Breaks ({breaks.length})</div>
      {breaks.map((b, i) => {
        const ongoing = b.isOpen || !b.breakEnd;
        const dur = ongoing
          ? secToHhmm(Math.max(0, moment().diff(moment(b.breakStartAt), "seconds")))
          : secToHhmm(hmsToSec(b.duration));
        return (
          <div className="brk-detail-row" key={b.id || i}>
            <span className="brk-idx">{i + 1}.</span>
            <span className="brk-lbl">Break</span>
            <span className="log-time" title={b.breakStartAt || ""}>
              {fmtClock(b.breakStart) || "—"}
            </span>
            <svg className="brk-arrow">
              <use href="#i-arr" />
            </svg>
            <span className="brk-lbl">Back</span>
            <span className="log-time" title={b.breakEndAt || ""}>
              {ongoing ? <em className="brk-ongoing">on break</em> : fmtClock(b.breakEnd)}
            </span>
            <span className="brk-dur">{dur}</span>
          </div>
        );
      })}
    </div>
  );
};
